import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import usePriceMode from '@/app/components/search/hooks/usePriceMode.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { getAdultsChildrenCount } from '@/utils/peopleUtils.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { FilterDataRange } from '@/app/common/filters/core/filtersTypes.ts';

// DP budget values are per person: filterByPriceRange multiplies both PRICE bounds by
// peopleCountDivider (adults + children aged >= 2, matching getAdultsChildrenCount) and compares
// them against floor(total), so total mode is display-only. Bounds are widened (min floored, max
// ceiled) rather than rounded, so the stored range always covers what the user selected.
export const usePriceFilterScale = (filterName: string) => {
  const { searchParams } = usePageWrapperData();
  const { totalPriceMode, showTotalPrice } = usePriceMode();

  // Active whenever the funnel is on total prices: the price toggler, or any family search.
  const active = filterName === FILTER_NAME.PRICE_RANGE && totalPriceMode && showTotalPrice;
  const pax = active ? Math.max(1, getAdultsChildrenCount(searchParams)) : 1;

  return {
    active,
    pax,
    scale: (n: number) => (active ? n * pax : n),
    unscaleRange: (value: { min: number; max: number }) =>
      active
        ? { min: Math.floor(value.min / pax), max: Math.ceil(value.max / pax) }
        : { min: value.min, max: value.max },
  };
};

// The budget label is resolved server-side per search mode (price_person for DP), so every place that
// titles the budget filter — pill overlay header and group heading — needs the displayed unit instead.
// Returns undefined while the range is shown per person, i.e. keep the server label.
export const usePriceFilterTitle = (): string | undefined => {
  const { t } = useTranslation();
  const { active } = usePriceFilterScale(FILTER_NAME.PRICE_RANGE);

  return active ? t('vacation.oss.components.searchfilters.labels.total_price') : undefined;
};

export const scaleRangeData = (data: FilterDataRange, scale: (n: number) => number): FilterDataRange => ({
  ...data,
  value: { min: scale(data.value.min), max: scale(data.value.max) },
  default: { min: scale(data.default.min), max: scale(data.default.max) },
  allowed: { min: scale(data.allowed.min), max: scale(data.allowed.max) },
  maxValue: scale(data.maxValue),
  ...(data.dynamicValues
    ? {
        dynamicValues: {
          ...data.dynamicValues,
          from: scale(data.dynamicValues.from),
          to: scale(data.dynamicValues.to),
        },
      }
    : {}),
});
